import type { ActivityKey, SplitCategory, Weather } from '@/api/dashboard-live';
import { previousDayValue, usePilotage } from '../context';
import { AlertList, BigNumber, Delta, MiniStat, Meter, Panel, Row, SectionState, SeverityIcon, Sparkline, StackBar, Unavailable, WeatherCard } from '../ui';
import { DailyChart, Legend, TimeChart } from '../charts';
import { compact, dec, ms, num, pct } from '../format';
import { loadColor } from '../theme';
import {
  ACTIVITY_LABEL,
  activityDetail,
  apiDetail,
  infraDetail,
  migrationRateDetail,
  newUsersDetail,
  splitColors,
  splitDetail,
  SPLIT_LABEL,
  v1Detail,
  v2Detail,
  weatherDetail,
} from '../details';

const OVERVIEW_ACTIVITY: ActivityKey[] = ['attendanceCalls', 'grades', 'textbookEntries', 'messages', 'mails', 'invoices'];

export function OverviewScreen() {
  const { payload, palette: p, daily, day, openDetail } = usePilotage();
  const s = payload.sections;
  const v1 = s.v1?.data;
  const v2 = s.v2?.data;
  const t = s.v2Tenants?.data;
  const plat = s.platform?.data;
  const apis = s.apis?.data;
  const infra = s.infra?.data;

  const upstream = (k: string) => apis?.upstreams.find((u) => u.key === k);
  const probe = (k: string) => apis?.probes.find((x) => x.key === k);
  const prev = (m: string) => previousDayValue(daily, m, day);

  const weatherStats = (w: Weather) => {
    switch (w.key) {
      case 'v1':
        return (
          <>
            <MiniStat label="req/min" value={dec(upstream('v1')?.rpm)} />
            <MiniStat label="p95" value={ms(upstream('v1')?.p95Ms)} />
            <MiniStat label="5xx" value={pct(upstream('v1')?.errorRatePct, 2)} />
          </>
        );
      case 'v2':
        return (
          <>
            <MiniStat label="req/min" value={dec(upstream('v2-api')?.rpm)} />
            <MiniStat label="p95" value={ms(upstream('v2-api')?.p95Ms)} />
            <MiniStat label="5xx" value={pct(upstream('v2-api')?.errorRatePct, 2)} />
          </>
        );
      case 'data':
        return (
          <>
            <MiniStat label="sonde" value={ms(probe('data-api')?.latencyMs)} />
            <MiniStat label="appels/5 min" value={num(apis?.dataInternal?.requests5m)} />
            <MiniStat label="5xx" value={pct(apis?.dataInternal?.errorRatePct, 2)} />
          </>
        );
      case 'infra':
        return (
          <>
            <MiniStat label="CPU" value={pct(infra?.cluster.appCpuPct, 0)} />
            <MiniStat label="mémoire" value={pct(infra?.cluster.appMemPct, 0)} />
            <MiniStat label="nœuds" value={infra ? `${infra.cluster.nodesReady}/${infra.cluster.nodesTotal}` : '–'} />
          </>
        );
      case 'ops':
        return (
          <>
            <MiniStat label="sauveg. V1" value={num(plat?.ops.backups.v1)} />
            <MiniStat label="projets" value={num(plat?.migration.projects.running)} />
            <MiniStat label="collecte" value={ms(payload.durationMs)} />
          </>
        );
    }
  };

  const splitSegments = (Object.keys(SPLIT_LABEL) as SplitCategory[]).map((k) => ({
    key: k,
    label: SPLIT_LABEL[k],
    value: plat?.split.counts[k] ?? 0,
    color: splitColors(p)[k],
  }));

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)_minmax(0,1fr)] gap-3">
      {/* Météo */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {(['v1', 'v2', 'data', 'infra', 'ops'] as const).map((k) => {
          const w = payload.weather[k];
          return w ? (
            <WeatherCard key={k} weather={w} onOpen={() => openDetail(weatherDetail(w, payload))}>
              {weatherStats(w)}
            </WeatherCard>
          ) : null;
        })}
      </div>

      {/* Indicateurs clés */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Panel title="Actifs V2 aujourd'hui" right={<SectionState section={s.v2} />} onOpen={() => openDetail(v2Detail(payload, p))}>
          <BigNumber value={num(v2?.activeUsersToday)} className="text-4xl" />
          <div className="mt-1 flex items-center justify-between gap-2">
            <Delta value={v2?.activeUsersToday} previous={prev('v2.dau')} />
            <span className="text-xs text-slate-400">{num(v2?.online15m)} en ligne</span>
          </div>
          <Sparkline points={(daily['v2.dau'] ?? []).slice(-30)} color={p.v2} daily />
        </Panel>
        <Panel title="Actifs V1 aujourd'hui" right={<SectionState section={s.v1} />} onOpen={() => openDetail(v1Detail(payload, p))}>
          <BigNumber value={num(v1?.activeUsersToday)} className="text-4xl" />
          <div className="mt-1 flex items-center justify-between gap-2">
            <Delta value={v1?.activeUsersToday} previous={prev('v1.dau')} invert />
            <span className="text-xs text-slate-400">{num(v1?.online15m)} en ligne</span>
          </div>
          <Sparkline points={(daily['v1.dau'] ?? []).slice(-30)} color={p.v1} daily />
        </Panel>
        <Panel title="Part d'usage V2" subtitle="actifs V2 / (V1 + V2)" onOpen={() => openDetail(migrationRateDetail(payload, p))}>
          <BigNumber value={dec(plat?.rates.usageV2Pct, 1)} unit="%" className="text-4xl" />
          <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: p.v1 }}>
            <div className="h-full" style={{ width: `${plat?.rates.usageV2Pct ?? 0}%`, background: p.v2, borderRight: '2px solid var(--pilotage-surface)' }} />
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-slate-400">
            <span>V2 {num(plat?.rates.v2Dau)}</span>
            <span>V1 {num(plat?.rates.v1Dau)}</span>
          </div>
          <Sparkline points={(daily['migration.usage_v2_pct'] ?? []).slice(-30)} color={p.v2} daily height={26} format={(n) => `${dec(n)} %`} />
        </Panel>
        <Panel title="Écoles basculées" subtitle="≥ 80 % des actifs du jour en V2" onOpen={() => openDetail(migrationRateDetail(payload, p))}>
          <BigNumber value={dec(plat?.rates.schoolsMostlyV2Pct ?? plat?.rates.schoolsV2OnlyPct, 0)} unit="%" className="text-4xl" />
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {num(plat?.rates.schoolsMostlyV2)} / {num(plat?.rates.schoolsActive)} écoles actives · {num(plat?.split.counts.v2Only)} 100 % V2 · {num(plat?.split.counts.v1Only)} en V1 seule
          </div>
          <Sparkline points={(daily['migration.schools_mostly_v2_pct'] ?? []).slice(-30)} color={p.v2} daily height={30} format={(n) => `${dec(n)} %`} />
        </Panel>
        <Panel title="Nouveaux V2 (jour)" right={<SectionState section={s.v2Tenants} />} onOpen={() => openDetail(newUsersDetail(payload))}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <BigNumber value={num(t?.newToday.student)} className="text-3xl" />
              <div className="text-xs text-slate-400">élèves</div>
            </div>
            <div>
              <BigNumber value={num(t?.newToday.parent)} className="text-3xl" />
              <div className="text-xs text-slate-400">parents</div>
            </div>
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {num(t?.newToday.teacher)} enseignants · {num(v2?.accountsCreatedToday)} comptes créés
          </div>
          <Sparkline points={(daily['v2.new.student'] ?? []).slice(-30)} color={p.v2} daily height={26} />
        </Panel>
        <Panel title="Parc" subtitle="écoles en production" onOpen={() => openDetail(splitDetail(payload, p))}>
          <BigNumber value={num(plat?.fleet.total)} className="text-4xl" />
          <div className="mt-1 space-y-0.5">
            <Row label="Élèves V2 (année en cours)" value={compact(t?.totals.student)} />
            <Row label="Parents V2" value={compact(t?.totals.parent)} />
            <Row label="Écoles V1 sans V2" value={num(plat?.fleet.v1OnlyProduction)} />
          </div>
        </Panel>
      </div>

      {/* Usage */}
      <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-12">
        <Panel
          title="Utilisateurs actifs par jour"
          subtitle="fin de journée, 60 j · V1 complète (web + mobile) à partir du 01/10, connexions explicites avant"
          className="xl:col-span-6"
          right={
            <Legend
              defs={[
                { metric: 'v2.dau', label: 'V2', color: p.v2 },
                { metric: 'v1.dau', label: 'V1', color: p.v1 },
              ]}
            />
          }
          onOpen={() => openDetail(migrationRateDetail(payload, p))}
        >
          <DailyChart
            defs={[
              { metric: 'v2.dau', label: 'V2', color: p.v2 },
              { metric: 'v1.dau', label: 'V1', color: p.v1 },
            ]}
            days={60}
            height="100%"
          />
        </Panel>
        <Panel title="Où se connectent les écoles" subtitle="écoles en production, aujourd'hui" className="xl:col-span-3" right={<SectionState section={s.platform} />} onOpen={() => openDetail(splitDetail(payload, p))}>
          {plat ? (
            <div className="flex h-full flex-col justify-between gap-3">
              <StackBar segments={splitSegments} height={18} />
              <div className="space-y-1">
                {plat.split.schools
                  .filter((x) => x.category === 'v1Only')
                  .slice(0, 5)
                  .map((x) => (
                    <Row key={x.schoolId} label={<span title={x.name}>{x.code}</span>} value={`${num(x.v1Users)} en V1`} swatch={p.v1} />
                  ))}
                {plat.split.counts.v1Only > 5 && <p className="text-[11px] text-slate-400">+ {num(plat.split.counts.v1Only - 5)} autres écoles en V1 seule</p>}
              </div>
            </div>
          ) : (
            <Unavailable section={s.platform} what="Répartition" />
          )}
        </Panel>
        <Panel title="Activité V2 aujourd'hui" className="xl:col-span-3" right={<SectionState section={s.v2Tenants} />} onOpen={() => openDetail(activityDetail(payload))}>
          {t ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {OVERVIEW_ACTIVITY.map((k) => (
                <button
                  key={k}
                  type="button"
                  className="rounded-lg px-1 py-0.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetail(activityDetail(payload, k));
                  }}
                >
                  <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{compact(t.activity[k])}</div>
                  <div className="text-xs text-slate-400">{ACTIVITY_LABEL[k]}</div>
                </button>
              ))}
              {t.activity.mailsFailed > 0 && (
                <div className="col-span-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <SeverityIcon severity={t.activity.mailsFailed >= 20 ? 'warning' : 'info'} className="h-3.5 w-3.5" />
                  {num(t.activity.mailsFailed)} e-mails en échec
                </div>
              )}
            </div>
          ) : (
            <Unavailable section={s.v2Tenants} what="Activité" />
          )}
        </Panel>
      </div>

      {/* Technique */}
      <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-12">
        <Panel
          title="Temps de réponse p95 (24 h)"
          subtitle="trafic réel, logs d'ingress"
          className="xl:col-span-5"
          right={
            <Legend
              defs={[
                { metric: 'api.v2-api.p95_ms', label: 'API V2', color: p.v2 },
                { metric: 'api.v1.p95_ms', label: 'V1', color: p.v1 },
                { metric: 'api.data.p95_ms', label: 'API data', color: p.third },
              ]}
            />
          }
          onOpen={() => openDetail(apiDetail(payload, p, 'v2-api'))}
        >
          <TimeChart
            defs={[
              { metric: 'api.v2-api.p95_ms', label: 'API V2', color: p.v2 },
              { metric: 'api.v1.p95_ms', label: 'V1', color: p.v1 },
              { metric: 'api.data.p95_ms', label: 'API data', color: p.third },
            ]}
            format={ms}
            height="100%"
          />
        </Panel>
        <Panel title="Ressources" subtitle="pool applicatif AKS" className="xl:col-span-3" right={<SectionState section={s.infra} />} onOpen={() => openDetail(infraDetail(payload, p))}>
          {infra ? (
            <div className="flex h-full flex-col justify-around gap-2">
              <Meter label="CPU moyen" value={infra.cluster.appCpuPct} color={loadColor(infra.cluster.appCpuPct)} />
              <Meter label="Mémoire moyenne" value={infra.cluster.appMemPct} color={loadColor(infra.cluster.appMemPct, 80, 90)} />
              <Meter label="Disque le plus plein" value={infra.cluster.diskPctMax} color={loadColor(infra.cluster.diskPctMax, 80, 90)} />
              <Meter
                label="Pods / capacité"
                value={infra.cluster.appPodCapacity ? (100 * infra.cluster.appPods) / infra.cluster.appPodCapacity : null}
                color={loadColor(infra.cluster.appPodCapacity ? (100 * infra.cluster.appPods) / infra.cluster.appPodCapacity : null, 80, 90)}
                text={`${num(infra.cluster.appPods)} / ${num(infra.cluster.appPodCapacity)}`}
              />
            </div>
          ) : (
            <Unavailable section={s.infra} what="Infrastructure" />
          )}
        </Panel>
        <Panel title="Alertes ouvertes" className="xl:col-span-4" bodyClassName="overflow-y-auto" right={<span className="text-xs tabular-nums text-slate-400">{num(payload.alerts.length)}</span>}>
          <AlertList alerts={payload.alerts.slice(0, 8)} />
        </Panel>
      </div>
    </div>
  );
}
